import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import {
  Bug,
  ExternalLink,
  MessageSquare,
  RefreshCw,
  ThumbsUp
} from 'lucide-react';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import { useGetZapfeedPosts } from '@utils/api/zapfeed/use-get-zapfeed-posts';
import { ZAPFEED_URL } from '@utils/constants/app-constants';
import {
  ZapfeedPost,
  ZapfeedPostStatus
} from '@utils/types/back-office/zapfeed';

/* eslint-disable no-restricted-syntax -- Status colors mirror the Zapfeed board and must stay identical in both themes */
const STATUS_COLORS: Record<ZapfeedPostStatus, string> = {
  NEW: '#3B82F6',
  OPEN: '#3B82F6',
  UNDER_REVIEW: '#8B5CF6',
  PLANNED: '#F59E0B',
  IN_PROGRESS: '#10B981',
  COMPLETED: '#6B7280',
  DECLINED: '#EF4444',
  CLOSED: '#6B7280'
};
const BUG_ACCENT = '#EF4444';
/* eslint-enable no-restricted-syntax */

/** How many open items the list shows before "view all" takes over. */
const VISIBLE_POSTS = 6;

const statusLabelKey: Record<ZapfeedPostStatus, string> = {
  NEW: 'new',
  OPEN: 'open',
  UNDER_REVIEW: 'under-review',
  PLANNED: 'planned',
  IN_PROGRESS: 'in-progress',
  COMPLETED: 'completed',
  DECLINED: 'declined',
  CLOSED: 'closed'
};

const relativeDate = (value: string) => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return formatDistanceToNow(parsed, { addSuffix: true });
};

/**
 * Bug Board — recent unresolved reports from the Scriptix Zapfeed board.
 * Rendered on the back-office dashboard only when both VITE_ZAPFEED_URL and
 * VITE_ZAPFEED_API_KEY are set at build time.
 */
export default function BugBoardWidget() {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const { data, isLoading, isFetching, isError, refetch } =
    useGetZapfeedPosts();

  const boardUrl = useMemo(() => {
    const slug = data?.posts?.find(p => p.board?.slug)?.board?.slug;
    return slug ? `${ZAPFEED_URL}/?board=${slug}` : ZAPFEED_URL;
  }, [data]);

  const visiblePosts: ZapfeedPost[] = (data?.openPosts ?? []).slice(
    0,
    VISIBLE_POSTS
  );

  return (
    <div className="rounded-2xl border border-hairline bg-app-soft shadow-card p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span
            className="flex h-8 w-8 items-center justify-center rounded-xl"
            style={{ backgroundColor: `${BUG_ACCENT}1A`, color: BUG_ACCENT }}
          >
            <Bug className="h-4 w-4" />
          </span>
          <div>
            <p
              className="text-sm font-semibold"
              style={{ color: getColour('normalText') }}
            >
              {t('bug-board')}
            </p>
            <p className="text-xs" style={{ color: getColour('inactiveText') }}>
              {isLoading
                ? t('loading')
                : `${data?.openCount ?? 0} ${t('open-reports')} · ${
                    data?.bugCount ?? 0
                  } ${t('bugs')}`}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex items-center gap-1 px-2.5 py-1 rounded-full border border-hairline bg-app text-xs transition-opacity hover:opacity-80 disabled:opacity-60"
            style={{ color: getColour('inactiveText') }}
            title={t('refresh')}
          >
            <RefreshCw
              className={`h-3 w-3 ${isFetching ? 'animate-spin' : ''}`}
            />
          </button>
          <a
            href={boardUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="flex items-center gap-1 px-2.5 py-1 rounded-full border border-hairline bg-app text-xs transition-opacity hover:opacity-80"
            style={{ color: getColour('inactiveText') }}
          >
            {t('view-all')}
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </div>

      {isError && (
        <p className="text-sm" style={{ color: getColour('inactiveText') }}>
          {t('bug-board-unavailable')}
        </p>
      )}

      {!isError && isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, index) => (
            <div
              key={index}
              className="h-12 rounded-xl border border-hairline bg-app animate-pulse"
            />
          ))}
        </div>
      )}

      {!isError && !isLoading && visiblePosts.length === 0 && (
        <p className="text-sm" style={{ color: getColour('inactiveText') }}>
          {t('no-open-reports')}
        </p>
      )}

      {!isError && !isLoading && visiblePosts.length > 0 && (
        <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {visiblePosts.map(post => (
            <li key={post.id}>
              <a
                href={`${ZAPFEED_URL}/posts/${post.id}`}
                target="_blank"
                rel="noreferrer noopener"
                className="flex items-start justify-between gap-3 rounded-xl border border-hairline bg-app px-3 py-2 transition-opacity hover:opacity-80"
              >
                <div className="min-w-0">
                  <p
                    className="truncate text-sm font-medium"
                    style={{ color: getColour('normalText') }}
                  >
                    {post.title}
                  </p>
                  <div
                    className="mt-1 flex flex-wrap items-center gap-2 text-xs"
                    style={{ color: getColour('inactiveText') }}
                  >
                    <span
                      className="rounded-full px-2 py-0.5"
                      style={{
                        backgroundColor: `${STATUS_COLORS[post.status]}1A`,
                        color: STATUS_COLORS[post.status]
                      }}
                    >
                      {t(statusLabelKey[post.status])}
                    </span>
                    {post.type === 'BUG' && (
                      <span
                        className="rounded-full px-2 py-0.5"
                        style={{
                          backgroundColor: `${BUG_ACCENT}1A`,
                          color: BUG_ACCENT
                        }}
                      >
                        {t('bug')}
                      </span>
                    )}
                    <span>{relativeDate(post.createdAt)}</span>
                  </div>
                </div>

                <div
                  className="flex shrink-0 items-center gap-3 text-xs"
                  style={{ color: getColour('inactiveText') }}
                >
                  <span className="flex items-center gap-1">
                    <ThumbsUp className="h-3 w-3" />
                    {post.voteCount}
                  </span>
                  <span className="flex items-center gap-1">
                    <MessageSquare className="h-3 w-3" />
                    {post._count?.comments ?? 0}
                  </span>
                </div>
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
